/**
 * **Two completion rings on the Trendy tab**, each on the section whose numbers it summarises.
 *
 * ── They were CONCENTRIC for one day, and the owner could not read them ───────────────────────
 * The Apple-Health drawing asked for on the morning of 1 September 2026 came back the same day:
 * "ja nie wiem co ona przedstawia aktualnie zupełnie." Two arcs about two different denominators,
 * sharing a centre and carrying their words in a legend to the side, do not say which arc is which
 * — and the percentage was nowhere on the drawing at all.
 *
 * So the pair is split. Frekwencja keeps its ring on the Frekwencja card, with the percentage in
 * the middle, the three counters beside it and the denominator in words underneath. The volume
 * ring moves to the Objętość mięśniowa section — "przenieś ten krążek jakoś ładnie koło objętości
 * mięśniowej" — where the MEV/MAV tracks it summarises are the next thing under it, so the reader
 * does not have to be told what the fraction is about.
 *
 * The projection is unchanged by that move: the same two fractions, the same denominators, the
 * same refusal to draw one that does not exist. What changed is that they are handed over BY NAME
 * rather than as a list, because they are no longer one picture.
 *
 * ── A ring is a FRACTION WITH A REAL DENOMINATOR, or it is not drawn ──────────────────────────
 * That is the whole rule this module exists to hold. A ring closes; something has to say what
 * closing means. Tonnage, a count of records and a training streak were all considered and all
 * refused for the same reason — none of them has a target, so the only way to draw one as a ring is
 * to invent a maximum, and an invented maximum is a picture that lies at a glance and cannot be
 * caught by reading it.
 *
 * Two survive with denominators the app already computes:
 *
 * | Ring | Numerator | Denominator | Who sets it |
 * |---|---|---|---|
 * | Frekwencja | days trained in the window | days that asked for a session | the plan |
 * | Objętość | muscle groups inside the optimal band | muscle groups trained at all | the MEV/MAV cuts |
 *
 * ── The check-in ring is NOT drawn, and the field it needs has a name ─────────────────────────
 * A third ring — check-ins completed over check-ins due — has no honest
 * denominator on this contract, and the reason is structural rather than a missing column:
 *
 * · `CheckInSchedule` carries `NextDueDate` as a mutable CURSOR and no log of the occurrences it
 *   has passed, and neither it nor `CheckInSchedule.CreatedAt` is published by any endpoint.
 * · `SurveyProcessor.MaterializeScheduleAsync` mints exactly ONE assignment for a gap however long
 *   it is, so counting materialised rows undercounts every client who went quiet.
 * · Materialisation runs only from the CUSTOMER's own pending-surveys read, which a trainer cannot
 *   call — so what `/customer/{id}/surveys` shows a trainer is stale by construction.
 *
 * Drawing it anyway would have meant choosing between a denominator that is too small and one that
 * is guessed. Two rings, and the gap is reported.
 *
 * ── Overshoot is KEPT, never clamped ──────────────────────────────────────────────────────────
 * A client who trained more days than the plan asked for is above 100%, and `pct` says so. The
 * clamping belongs to the drawing; the projection's job is to not lose the fact on the way. A ring
 * that quietly reported 100% for 113% would tell the trainer their client did exactly what was
 * asked. The drawing used to keep that honest with a second arc lapping the first, which went with
 * the concentric pair: the percentage now stands in the middle of the ring, so a closed arc under
 * "113%" states the excess where an unlabelled closed arc could only hide it.
 *
 * ── Zero is a reading; absence is not ─────────────────────────────────────────────────────────
 * `total === 0` means nothing asked anything of this client in the window, and there is no
 * fraction — the ring is omitted. `value === 0` with a real total is a genuine nought per cent and
 * is drawn as one. Collapsing the two would tell a trainer their client missed everything when in
 * fact nothing was scheduled.
 */

import type { AttendanceWindow } from './attendance';
import type { MuscleVolumeView } from './muscleVolume';

export interface ActivityRing {
  /** How many of the denominator were reached. Never negative. */
  readonly value: number;
  /** What the ring closes at. Always greater than zero — a ring with no denominator is omitted. */
  readonly total: number;
  /** `value / total` as a whole percent. **Not clamped**: 113 means 113. */
  readonly pct: number;
}

/**
 * The two rings, by name.
 *
 * A list would let a caller draw "the first ring", and there is no longer a place that draws both:
 * each section takes the one it is about. `null` is a ring with no denominator in this window — the
 * section says so in words instead of drawing an arc at zero.
 */
export interface ActivityRingsView {
  readonly attendance: ActivityRing | null;
  readonly volume: ActivityRing | null;
}

export interface ActivityRingSources {
  /** The window's attendance fold, or `null` while `/consistency` is in flight or after it failed. */
  readonly attendance: AttendanceWindow | null;
  /** The window's muscle volume, or `null` for the same two reasons. */
  readonly volume: MuscleVolumeView | null;
}

function ringOf(value: number, total: number): ActivityRing | null {
  if (total <= 0) return null;
  return { value, total, pct: Math.round((value / total) * 100) };
}

export function buildActivityRings(sources: ActivityRingSources): ActivityRingsView {
  const attendance = sources.attendance;
  const volume = sources.volume;

  /**
   * Groups the client actually trained in the window, and how many of those landed in the optimal
   * band. The denominator is TRAINED groups rather than every group the contract names: a chest the
   * plan never touched is not a chest that fell short of its target, and counting it would make the
   * ring measure the plan's breadth rather than whether the work landed where it should.
   */
  const trained =
    volume === null ? [] : volume.clusters.flatMap((cluster) => cluster.rows).filter(
      (row) => row.zone !== 'none',
    );

  return {
    attendance: attendance === null ? null : ringOf(attendance.done, attendance.total),
    volume:
      volume === null
        ? null
        : ringOf(trained.filter((row) => row.zone === 'opt').length, trained.length),
  };
}
